// Firebase Functions API client for TMDB operations
// This replaces all direct TMDB API calls with secure Firebase Functions

import { Capacitor } from '@capacitor/core';
import {
  formatMovieDisplayTitleFromId,
  getTmdbNumericIdForCache,
  unifiedHttpsCallable,
} from '@watchlist/shared';
import { fetchWithRetry, isRateLimitError, getRateLimitMessage } from '../utils/httpRetry';

// =============== CONFIGURATION ===============

// Check if we should use Firebase Functions API
export function shouldUseFunctionsAPI(): boolean {
  // ALWAYS use Functions API in production for security
  if (process.env.NODE_ENV === 'production') {
    return true;
  }

  // In development, check if explicitly enabled or if no direct API key is available
  const useFunc = process.env.REACT_APP_USE_FUNCTIONS_API === 'true';
  const hasDirectApi = process.env.REACT_APP_TMDB_API_KEY && process.env.REACT_APP_TMDB_API_KEY.length > 0;

  // Use Functions API if explicitly enabled, or if no direct API key
  // This allows local development with direct API key for faster iteration
  return useFunc || !hasDirectApi;

  // Original logic kept for reference but commented out:
  // // In production, always use Functions API for security
  // if (process.env.NODE_ENV === 'production') {
  //   return true;
  // }
  // 
  // // In development, check if explicitly enabled or if no direct API key is available
  // const useFunc = process.env.REACT_APP_USE_FUNCTIONS_API === 'true';
  // const hasDirectApi = process.env.REACT_APP_TMDB_API_KEY && process.env.REACT_APP_TMDB_API_KEY.length > 0;
  // 
  // // Always use Functions API if explicitly enabled, or if no direct API key
  // return useFunc || !hasDirectApi;
}

// Get the Functions base URL
function getFunctionUrl(functionName: string): string {
  const isDev = process.env.NODE_ENV === 'development';

  // Try local emulator first in development, fallback to production
  if (isDev) {
    // Check if local emulator might be running by testing environment
    const useLocal = process.env.REACT_APP_USE_LOCAL_FUNCTIONS === 'true';
    if (useLocal) {
      return `http://127.0.0.1:5001/watchlists-1c639/us-central1/${functionName}`;
    }
  }

  // Always use production Functions URL for reliability
  return `https://us-central1-watchlists-1c639.cloudfunctions.net/${functionName}`;
}

// =============== TYPES ===============

export interface MovieSearchResult {
  results: any[];
  page: number;
  total_pages: number;
  total_results: number;
  cached?: boolean;
}

export interface MovieDetails {
  id: number;
  title: string;
  release_date: string;
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  genres: Array<{ id: number; name: string }>;
  credits: {
    cast: Array<{ id: number; name: string; character: string; profile_path: string | null }>;
    crew: Array<{ id: number; name: string; job: string; department: string }>;
  };
  vote_average: number;
  vote_count: number;
  runtime: number;
  [key: string]: any;
}

export interface PersonSearchResult {
  page: number;
  results: Array<{
    id: number;
    name: string;
    profile_path: string | null;
    adult: boolean;
    popularity: number;
    known_for_department: string;
    gender: 0 | 1 | 2 | 3; // 0: Not set, 1: Female, 2: Male, 3: Non-binary
    known_for: Array<{
      id: number;
      title?: string;
      name?: string;
      poster_path: string | null;
      media_type: 'movie' | 'tv';
    }>;
  }>;
  total_pages: number;
  total_results: number;
  cached?: boolean;
}

export interface PersonDetails {
  id: number;
  name: string;
  biography: string;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  adult: boolean;
  imdb_id: string | null;
  homepage: string | null;
  known_for_department: string;
  popularity: number;
  gender: 0 | 1 | 2 | 3; // 0: Not set, 1: Female, 2: Male, 3: Non-binary
  also_known_as: string[];
  external_ids?: {
    imdb_id: string | null;
    facebook_id: string | null;
    instagram_id: string | null;
    twitter_id: string | null;
  };
  images?: {
    profiles: Array<{
      file_path: string;
      width: number;
      height: number;
      aspect_ratio: number;
      vote_average: number;
      vote_count: number;
    }>;
  };
  movie_credits?: {
    cast: Array<{
      id: number;
      title: string;
      character: string;
      poster_path: string | null;
      release_date: string;
      vote_average: number;
      adult: boolean;
      credit_id: string;
    }>;
    crew: Array<{
      id: number;
      title: string;
      job: string;
      department: string;
      poster_path: string | null;
      release_date: string;
      vote_average: number;
      adult: boolean;
      credit_id: string;
    }>;
  };
  [key: string]: any;
}

export interface GenreList {
  genres: Array<{ id: number; name: string }>;
}

export interface CompanySearchResult {
  page: number;
  results: Array<{
    id: number;
    name: string;
    logo_path: string | null;
    origin_country: string;
  }>;
  total_pages: number;
  total_results: number;
}

export interface KeywordSearchResult {
  page: number;
  results: Array<{
    id: number;
    name: string;
  }>;
  total_pages: number;
  total_results: number;
}

export interface MultiSearchResult {
  page: number;
  results: Array<{
    id: number;
    media_type: 'movie' | 'tv' | 'person';
    title?: string;
    name?: string;
    poster_path?: string | null;
    profile_path?: string | null;
    [key: string]: any;
  }>;
  total_pages: number;
  total_results: number;
}

export interface TMDBConfiguration {
  images: {
    base_url: string;
    secure_base_url: string;
    backdrop_sizes: string[];
    logo_sizes: string[];
    poster_sizes: string[];
    profile_sizes: string[];
    still_sizes: string[];
  };
  change_keys: string[];
}

export interface MovieImages {
  id: number;
  backdrops: Array<{
    file_path: string;
    width: number;
    height: number;
    aspect_ratio: number;
    vote_average: number;
    vote_count: number;
  }>;
  posters: Array<{
    file_path: string;
    width: number;
    height: number;
    aspect_ratio: number;
    vote_average: number;
    vote_count: number;
  }>;
}

// =============== HELPER FUNCTIONS ===============

/**
 * Fast HTTP request for search - no retries for minimum latency
 * Used for search operations where speed is critical
 */
async function httpRequestFast(functionName: string, params: Record<string, string> = {}): Promise<any> {
  const url = getFunctionUrl(functionName);
  const queryParams = new URLSearchParams(params);
  const fullUrl = `${url}?${queryParams}`;

  // Omit Content-Type header on GET — it forces a CORS preflight (OPTIONS) round-trip
  // before every request, costing 150–400 ms on mobile. Without it, this is a "simple"
  // CORS request and the browser sends the GET directly.
  const response = await fetch(fullUrl, { method: 'GET' });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Make HTTP request to Firebase Functions with automatic retry for rate limits
 */
async function httpRequest(functionName: string, params: Record<string, string> = {}): Promise<any> {
  const url = getFunctionUrl(functionName);
  const queryParams = new URLSearchParams(params);
  const fullUrl = `${url}?${queryParams}`;

  try {
    // Same reasoning as httpRequestFast: no Content-Type on GET to avoid preflight.
    const response = await fetchWithRetry(fullUrl, {
      method: 'GET',
    }, {
      maxRetries: 3,
      initialDelayMs: 1000,
      maxDelayMs: 30000,
      backoffMultiplier: 2,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      // Handle 429 specifically with user-friendly message
      if (response.status === 429) {
        console.warn(`⚠️ Rate limited on ${functionName}:`, errorData);
        const rateLimitError = new Error(getRateLimitMessage(errorData)) as any;
        rateLimitError.status = 429;
        rateLimitError.retryAfter = errorData.retryAfter;
        rateLimitError.resetTime = errorData.resetTime;
        throw rateLimitError;
      }

      console.error(`❌ Functions API Error: ${functionName}`, {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });

      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`✅ Functions API Success: ${functionName}`, {
      resultCount: data.results?.length || 'N/A',
      cached: data.cached || false
    });

    return data;
  } catch (error) {
    // Provide better error message for rate limits
    if (isRateLimitError(error)) {
      console.warn(`⚠️ Rate limit error on ${functionName}:`, error);
      throw error; // Re-throw with the user-friendly message already set
    }

    console.error(`💥 Functions API Request Failed: ${functionName}`, {
      url: fullUrl,
      error: error instanceof Error ? error.message : 'Unknown error',
      params
    });
    throw error;
  }
}

/**
 * Extract numeric TMDB ID from various ID formats
 */
function extractTmdbId(movieId: number | string): string {
  if (typeof movieId === 'number') {
    return movieId.toString();
  }

  const idStr = movieId.toString();
  const numeric = getTmdbNumericIdForCache(idStr);
  if (numeric) {
    return numeric;
  }

  throw new Error(`Invalid movie ID format: ${movieId}`);
}

// =============== MOVIE FUNCTIONS ===============

/**
 * On iOS/Android, prefer batchGetMovieDetails (native Functions plugin) over raw fetch to the
 * HTTP getMovieDetails URL — WebView fetch to cloudfunctions.net is less reliable than callables.
 */
async function getMovieDetailsViaNativeBatchCallable(movieIdStr: string): Promise<MovieDetails | null> {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  try {
    const { getAuth } = await import('./firebase');
    const auth = await getAuth();
    if (!auth?.currentUser) {
      return null;
    }

    const batchGetMovies = unifiedHttpsCallable<
      { movieIds: string[] },
      { movies: Record<string, MovieDetails> }
    >('batchGetMovieDetails', { timeout: 30_000, region: 'us-central1' });

    const result = await batchGetMovies({ movieIds: [movieIdStr] });
    const movie = result.data?.movies?.[movieIdStr];
    if (movie && typeof movie === 'object' && (movie as MovieDetails).id != null) {
      return movie as MovieDetails;
    }
  } catch (e) {
    console.warn('getMovieDetails: native batchGetMovieDetails failed, will try HTTP endpoint', e);
  }
  return null;
}

/**
 * Search for movies using Firebase Functions
 *
 * On iOS/Android prefers the Capacitor Firebase callable plugin over WebView fetch to
 * cloudfunctions.net (less reliable / slower on native). Web continues to use the HTTP
 * endpoint directly since browser fetch is fastest there.
 */
export async function searchMovies(
  query: string,
  page: number = 1,
  language: string = 'en-US'
): Promise<MovieSearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }

  const params = {
    query: query.trim(),
    page: page.toString(),
    language,
  };

  if (Capacitor.isNativePlatform()) {
    try {
      const callable = unifiedHttpsCallable<
        { query: string; page: string; language: string },
        MovieSearchResult
      >('searchMoviesCallable', { timeout: 15_000, region: 'us-central1' });
      const result = await callable(params);
      return result.data as MovieSearchResult;
    } catch (e) {
      console.warn('searchMovies: native callable failed, falling back to HTTP', e);
    }
  }

  // OPTIMIZATION: Use fast request without retries for search - speed is critical
  return httpRequestFast('searchMovies', params);
}

/**
 * Get movie details using Firebase Functions
 */
export async function getMovieDetails(
  movieId: number | string,
  options: {
    language?: string;
    includeVideos?: boolean;
    includeCredits?: boolean;
  } = {}
): Promise<MovieDetails> {
  if (!movieId) {
    throw new Error('Movie ID is required');
  }

  const { language = 'en-US', includeVideos = false, includeCredits = true } = options;

  const idStr = movieId.toString();

  const fromBatch = await getMovieDetailsViaNativeBatchCallable(idStr);
  if (fromBatch) {
    console.log('🎬 Movie details from batchGetMovieDetails (native callable)', { movieId: idStr });
    return fromBatch;
  }

  // Handle IMDb IDs by converting them to TMDb IDs first
  let numericId: string;

  if (idStr.startsWith('tt')) {
    // This is an IMDb ID - convert to TMDb ID first
    console.log(`🔍 Converting IMDb ID ${idStr} to TMDb ID`);
    try {
      const findResult = await findMovieByExternalId(idStr, 'imdb_id', language);
      if (findResult.movie_results && findResult.movie_results.length > 0) {
        numericId = findResult.movie_results[0].id.toString();
        console.log(`✅ Converted IMDb ID ${idStr} to TMDb ID ${numericId}`);
      } else {
        throw new Error(`No TMDb movie found for IMDb ID: ${idStr}`);
      }
    } catch (error) {
      console.error(`❌ Failed to convert IMDb ID ${idStr} to TMDb ID:`, error);
      throw error;
    }
  } else {
    // Extract numeric TMDB ID for the Functions API
    numericId = extractTmdbId(movieId);
  }

  const url = getFunctionUrl(`getMovieDetails/${numericId}`);

  const queryParams = new URLSearchParams({
    language,
    include_videos: includeVideos.toString(),
    include_credits: includeCredits.toString()
  });
  const fullUrl = `${url}?${queryParams}`;

  console.log(`🎬 Getting movie details with options:`, { movieId, numericId, includeVideos, includeCredits, url: fullUrl });

  try {
    const response = await fetch(fullUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`🎬 Movie details response:`, {
      hasVideos: !!data.videos?.results?.length,
      videosCount: data.videos?.results?.length || 0,
      hasCredits: !!data.credits
    });

    return data;
  } catch (error) {
    console.error('Error getting movie details:', error);
    throw error;
  }
}

/**
 * Find movie by external ID (IMDB) using Firebase Functions
 */
export async function findMovieByExternalId(
  externalId: string,
  externalSource: string = 'imdb_id',
  language: string = 'en-US'
): Promise<any> {
  if (!externalId) {
    throw new Error('External ID is required');
  }

  const url = getFunctionUrl(`findMovieByExternalId/${externalId}`);
  const queryParams = new URLSearchParams({ external_source: externalSource, language });
  const fullUrl = `${url}?${queryParams}`;

  try {
    const response = await fetch(fullUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error finding movie by external ID:', error);
    throw error;
  }
}

/**
 * Get popular movies using Firebase Functions
 */
export async function getPopularMovies(
  page: number = 1,
  language: string = 'en-US',
  region: string = 'US'
): Promise<MovieSearchResult> {
  return httpRequest('getPopularMovies', {
    page: page.toString(),
    language,
    region
  });
}

/**
 * Get trending movies using Firebase Functions
 */
export async function getTrendingMovies(
  timeWindow: 'day' | 'week' = 'week',
  page: number = 1
): Promise<MovieSearchResult> {
  return httpRequest('getTrendingMovies', {
    time_window: timeWindow,
    page: page.toString()
  });
}

/**
 * Get now playing movies using Firebase Functions
 */
export async function getNowPlayingMovies(
  page: number = 1,
  language: string = 'en-US',
  region: string = 'US'
): Promise<MovieSearchResult> {
  return httpRequest('getNowPlayingMovies', {
    page: page.toString(),
    language,
    region
  });
}

/**
 * Discover movies using Firebase Functions
 */
export async function discoverMovies(params: {
  page?: number;
  language?: string;
  region?: string;
  sort_by?: string;
  include_adult?: boolean;
  primary_release_year?: number;
  with_genres?: string;
  without_genres?: string;
  with_companies?: string;
  with_keywords?: string;
  with_cast?: string;
  with_crew?: string;
  vote_average_gte?: number;
  vote_average_lte?: number;
  vote_count_gte?: number;
  release_date_gte?: string;
  release_date_lte?: string;
  [key: string]: any;
} = {}): Promise<MovieSearchResult> {
  const requestParams: Record<string, string> = {};

  // Convert all parameters to strings
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      requestParams[key] = params[key].toString();
    }
  });

  return httpRequest('discoverMovies', requestParams);
}

/**
 * Get movie images using Firebase Functions
 */
export async function getMovieImages(
  movieId: number | string,
  language: string = 'en-US'
): Promise<MovieImages> {
  const numericId = extractTmdbId(movieId);
  const url = getFunctionUrl(`getMovieImages/${numericId}`);
  const queryParams = new URLSearchParams({ language });
  const fullUrl = `${url}?${queryParams}`;

  try {
    const response = await fetch(fullUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting movie images:', error);
    throw error;
  }
}

/**
 * Get recently added movies (now playing) using Firebase Functions
 */
export async function getRecentlyAddedMovies(
  page: number = 1,
  language: string = 'en-US'
): Promise<MovieSearchResult> {
  return httpRequest('getRecentlyAddedMovies', {
    page: page.toString(),
    language
  });
}

// =============== PEOPLE FUNCTIONS ===============

/**
 * Search for people using Firebase Functions
 */
export async function searchPeople(
  query: string,
  page: number = 1,
  language: string = 'en-US'
): Promise<PersonSearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }

  const params = {
    query: query.trim(),
    page: page.toString(),
    language,
  };

  if (Capacitor.isNativePlatform()) {
    try {
      const callable = unifiedHttpsCallable<
        { query: string; page: string; language: string },
        PersonSearchResult
      >('searchPeopleCallable', { timeout: 15_000, region: 'us-central1' });
      const result = await callable(params);
      return result.data as PersonSearchResult;
    } catch (e) {
      console.warn('searchPeople: native callable failed, falling back to HTTP', e);
    }
  }

  // Match searchMovies: use the no-retry fast path. Search latency matters more
  // than a possibly-recoverable transient error — the debounced UI will retry.
  return httpRequestFast('searchPeople', params);
}

/**
 * Get person details using Firebase Functions
 */
export async function getPersonDetails(
  personId: number | string,
  language: string = 'en-US'
): Promise<PersonDetails> {
  if (!personId) {
    throw new Error('Person ID is required');
  }

  const numericId = personId.toString();
  const url = getFunctionUrl(`getPersonDetails/${numericId}`);
  const queryParams = new URLSearchParams({ language });
  const fullUrl = `${url}?${queryParams}`;

  try {
    const response = await fetch(fullUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting person details:', error);
    throw error;
  }
}

/**
 * Get person movie credits using Firebase Functions
 */
export async function getPersonMovieCredits(
  personId: number | string,
  language: string = 'en-US'
): Promise<any> {
  if (!personId) {
    throw new Error('Person ID is required');
  }

  const numericId = personId.toString();
  const url = getFunctionUrl(`getPersonMovieCredits/${numericId}`);
  const queryParams = new URLSearchParams({ language });
  const fullUrl = `${url}?${queryParams}`;

  try {
    const response = await fetch(fullUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting person movie credits:', error);
    throw error;
  }
}

// =============== GENERAL FUNCTIONS ===============

/**
 * Multi search using Firebase Functions
 */
export async function multiSearch(
  query: string,
  page: number = 1,
  language: string = 'en-US'
): Promise<MultiSearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }

  return httpRequest('multiSearch', {
    query: query.trim(),
    page: page.toString(),
    language
  });
}

/**
 * Get genres using Firebase Functions
 */
export async function getGenres(language: string = 'en-US'): Promise<GenreList> {
  return httpRequest('getGenres', { language });
}

/**
 * Get TMDB configuration using Firebase Functions
 */
export async function getConfiguration(): Promise<TMDBConfiguration> {
  return httpRequest('getConfiguration', {});
}

/**
 * Search companies using Firebase Functions
 */
export async function searchCompanies(
  query: string,
  page: number = 1
): Promise<CompanySearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }

  return httpRequest('searchCompanies', {
    query: query.trim(),
    page: page.toString()
  });
}

/**
 * Search keywords using Firebase Functions
 */
export async function searchKeywords(
  query: string,
  page: number = 1
): Promise<KeywordSearchResult> {
  if (!query.trim()) {
    throw new Error('Search query cannot be empty');
  }

  return httpRequest('searchKeywords', {
    query: query.trim(),
    page: page.toString()
  });
}

// =============== WRAPPER CLASS FOR COMPATIBILITY ===============

/**
 * Firebase Functions TMDb Service
 * Replaces direct TMDB API calls with Firebase Functions calls
 */
export class TMDbFunctionsService {
  private apiKeyValid: boolean = true;

  constructor() {
    console.log('TMDb Functions API initialized - using Firebase Functions for all TMDB operations');
  }

  // =============== MOVIE METHODS ===============

  async searchMovies(query: string, page: number = 1, includeCredits: boolean = false): Promise<{ Search: any[]; totalResults: string; Response: string }> {
    try {
      console.log(`🔍 TMDbFunctionsService.searchMovies called with query: "${query}"`);
      const data = await searchMovies(query, page);
      console.log('📊 Functions API raw response:', data);

      // Ensure we have valid results and convert them properly
      const rawResults = data.results || [];
      console.log('📋 Raw results from functions:', rawResults.slice(0, 2));

      // Convert each movie to ensure it has all required fields
      const convertedResults = rawResults.map((movie: any) => {
        // Extract year from release_date with fallback
        const year = movie.release_date && movie.release_date.trim()
          ? movie.release_date.split('-')[0]
          : new Date().getFullYear().toString();

        // Extract genres with fallback  
        const genres = movie.genre_ids && movie.genre_ids.length > 0
          ? this.mapGenreIdsToNames(movie.genre_ids)
          : 'Drama';

        // Ensure title exists
        const title = movie.title && movie.title.trim()
          ? movie.title.trim()
          : formatMovieDisplayTitleFromId(String(movie.id));

        // Create properly formatted movie object
        const formattedMovie = {
          ...movie,
          // Core TMDB fields
          title: title,
          id: movie.id,
          release_date: movie.release_date || '',

          // Standard compatibility fields with guaranteed values
          Title: title,
          Year: year,
          year: year,
          Genre: genres,
          // Remove hardcoded Runtime - let StandardMovieCard handle missing runtime data
          Plot: movie.overview && movie.overview.trim()
            ? movie.overview.trim()
            : 'Plot summary not available.',
          Poster: movie.poster_path
            ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
            : null,
          imdbID: `tmdb_${movie.id}`,
          imdbRating: movie.vote_average ? movie.vote_average.toFixed(1) : '7.0',
          Response: 'True',
          Type: 'movie'
        };

        console.log(`✅ Formatted movie:`, {
          Title: formattedMovie.Title,
          Year: formattedMovie.Year,
          Genre: formattedMovie.Genre,
          original: { title: movie.title, release_date: movie.release_date }
        });

        return formattedMovie;
      });

      console.log('✅ Functions API converted results:', convertedResults.slice(0, 2).map(m => ({
        Title: m.Title,
        Year: m.Year,
        Genre: m.Genre
      })));

      return {
        Search: convertedResults,
        totalResults: data.total_results?.toString() || '0',
        Response: 'True'
      };
    } catch (error) {
      console.error('❌ Error searching movies via Functions:', error);
      return {
        Search: [],
        totalResults: '0',
        Response: 'False'
      };
    }
  }

  /**
   * Enhanced search for movies using Firebase Functions with query analysis and multiple strategies.
   * Optional `actor` / `director` / `year` are sent as query params (or use inline `actor:`, `director:`, `year:` in `query`).
   */
  async enhancedSearchMovies(
    query: string,
    page: number = 1,
    opts?: { actor?: string; director?: string; year?: string }
  ): Promise<{ Search: Array<{ movie: any; relevanceScore: number; matchedFields: string[] }>; totalResults: string; Response: string; hasMore: boolean }> {
    try {
      // Detect language from query
      const detectedLanguage = this.detectLanguageFromQuery(query);

      // Build URL for enhanced search endpoint
      let url = `${getFunctionUrl('enhancedSearchMovies')}?query=${encodeURIComponent(query)}&page=${page}`;
      if (detectedLanguage) {
        url += `&language=${detectedLanguage}`;
      }
      if (opts?.actor?.trim()) {
        url += `&actor=${encodeURIComponent(opts.actor.trim())}`;
      }
      if (opts?.director?.trim()) {
        url += `&director=${encodeURIComponent(opts.director.trim())}`;
      }
      if (opts?.year?.trim()) {
        url += `&year=${encodeURIComponent(opts.year.trim())}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        // Fallback to basic search
        return await this.fallbackToBasicSearch(query, page);
      }

      const data = await response.json();

      // Check if the response has the expected structure
      if (data.results && Array.isArray(data.results)) {
        return this.processEnhancedResults(data, query);
      } else {
        console.warn('⚠️ Invalid response structure, falling back to basic search');
        return await this.fallbackToBasicSearch(query, page);
      }

    } catch (error) {
      console.error('❌ Enhanced search error:', error);
      return await this.fallbackToBasicSearch(query, page);
    }
  }

  /**
   * Fallback to basic search when enhanced search fails
   */
  private async fallbackToBasicSearch(query: string, page: number) {
    try {
      const data = await searchMovies(query, page);

      // Ensure we have valid results and convert them properly
      const rawResults = data.results || [];

      // Convert each movie and calculate relevance scores
      const enhancedResults = rawResults.map((movie: any, index: number) => {
        // Extract year from release_date with fallback
        const year = movie.release_date && movie.release_date.trim()
          ? movie.release_date.split('-')[0]
          : new Date().getFullYear().toString();

        // Extract genres with fallback  
        const genres = movie.genre_ids && movie.genre_ids.length > 0
          ? this.mapGenreIdsToNames(movie.genre_ids)
          : 'Drama';

        // Ensure title exists
        const title = movie.title && movie.title.trim()
          ? movie.title.trim()
          : formatMovieDisplayTitleFromId(String(movie.id));

        // Create properly formatted movie object
        const formattedMovie = {
          ...movie,
          // Core TMDB fields
          title: title,
          id: movie.id,
          release_date: movie.release_date || '',

          // Standard compatibility fields with guaranteed values
          Title: title,
          Year: year,
          year: year,
          Genre: genres,
          Plot: movie.overview && movie.overview.trim()
            ? movie.overview.trim()
            : 'Plot summary not available.',
          Poster: movie.poster_path
            ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
            : null,
          imdbID: `tmdb_${movie.id}`,
          imdbRating: movie.vote_average ? movie.vote_average.toFixed(1) : '7.0',
          Response: 'True',
          Type: 'movie'
        };

        // Calculate basic relevance score based on position and popularity
        const relevanceScore = this.calculateBasicRelevanceScore(movie, query, index);

        // Determine matched fields
        const matchedFields = this.getMatchedFields(movie, query);

        return {
          movie: formattedMovie,
          relevanceScore,
          matchedFields
        };
      });

      // Sort by relevance score
      const sortedResults = enhancedResults.sort((a, b) => b.relevanceScore - a.relevanceScore);

      // Calculate pagination
      const totalResults = parseInt(data.total_results?.toString() || '0');
      const resultsPerPage = 20;
      const hasMore = page * resultsPerPage < totalResults;

      console.log(`✅ Enhanced Functions API processed ${sortedResults.length} results with pagination info`);

      return {
        Search: sortedResults,
        totalResults: totalResults.toString(),
        Response: 'True',
        hasMore
      };
    } catch (error) {
      console.error('🚨 Fallback search failed:', error);
      return {
        Search: [],
        totalResults: '0',
        Response: 'False',
        hasMore: false
      };
    }
  }

  /**
   * Process enhanced search results from Firebase Functions
   */
  private processEnhancedResults(data: any, query: string) {
    const results = data.results || [];

    const enhancedResults = results.map((movie: any) => {
      // Format the movie object properly
      const formattedMovie = {
        ...movie,
        Title: movie.title || movie.Title,
        Year: movie.release_date ? movie.release_date.split('-')[0] : '',
        Genre: movie.genre_ids ? this.mapGenreIdsToNames(movie.genre_ids) : 'Drama',
        Plot: movie.overview || 'Plot summary not available.',
        Poster: movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null,
        imdbID: movie.imdbID || `tmdb_${movie.id}`,
        imdbRating: movie.vote_average ? movie.vote_average.toFixed(1) : '7.0',
        Response: 'True',
        Type: 'movie'
      };

      return {
        movie: formattedMovie,
        relevanceScore: movie.relevanceScore || movie.popularity || 0,
        matchedFields: movie.matchedFields || ['title']
      };
    });

    return {
      Search: enhancedResults,
      totalResults: data.total_results?.toString() || enhancedResults.length.toString(),
      Response: 'True',
      hasMore: data.hasMore || false
    };
  }

  /**
   * Detect language from search query
   */
  private detectLanguageFromQuery(query: string): string | null {
    const lowerQuery = query.toLowerCase();

    // Language detection mappings
    const languageMappings: { [key: string]: string } = {
      'malayalam': 'ml',
      'mollywood': 'ml',
      'hindi': 'hi',
      'bollywood': 'hi',
      'tamil': 'ta',
      'kollywood': 'ta',
      'telugu': 'te',
      'tollywood': 'te',
      'kannada': 'kn',
      'sandalwood': 'kn',
      'bengali': 'bn',
      'marathi': 'mr',
      'gujarati': 'gu',
      'punjabi': 'pa',
      'english': 'en',
      'spanish': 'es',
      'french': 'fr',
      'german': 'de',
      'italian': 'it',
      'japanese': 'ja',
      'korean': 'ko',
      'chinese': 'zh'
    };

    for (const [language, code] of Object.entries(languageMappings)) {
      if (lowerQuery.includes(language)) {
        return code;
      }
    }

    return null;
  }


  /**
   * Calculate basic relevance score for a movie based on query match
   */
  private calculateBasicRelevanceScore(movie: any, query: string, position: number): number {
    let score = 0;
    const queryLower = query.toLowerCase();
    const title = (movie.title || '').toLowerCase();
    const overview = (movie.overview || '').toLowerCase();

    // Title matches get highest score
    if (title.includes(queryLower)) {
      if (title === queryLower) score += 100; // Exact match
      else if (title.startsWith(queryLower)) score += 80; // Starts with query
      else score += 60; // Contains query
    }

    // Overview matches get medium score
    if (overview.includes(queryLower)) {
      score += 20;
    }

    // Popularity boost
    const popularity = movie.popularity || 0;
    score += Math.min(popularity / 100, 20); // Cap at 20 points

    // Vote average boost
    const voteAverage = movie.vote_average || 0;
    score += Math.min(voteAverage, 10); // Cap at 10 points

    // Position penalty (results later in the list get slightly lower scores)
    score -= position * 0.5;

    return Math.max(score, 0);
  }

  /**
   * Get matched fields for a movie based on query
   */
  private getMatchedFields(movie: any, query: string): string[] {
    const matchedFields: string[] = [];
    const queryLower = query.toLowerCase();

    if ((movie.title || '').toLowerCase().includes(queryLower)) {
      matchedFields.push('title');
    }

    if ((movie.overview || '').toLowerCase().includes(queryLower)) {
      matchedFields.push('overview');
    }

    // Check if query matches any genre names
    if (movie.genre_ids && movie.genre_ids.length > 0) {
      const genreNames = this.mapGenreIdsToNames(movie.genre_ids).toLowerCase();
      if (genreNames.includes(queryLower)) {
        matchedFields.push('genre');
      }
    }

    return matchedFields;
  }

  // Helper method to map genre IDs to names
  private mapGenreIdsToNames(genreIds: number[]): string {
    const genreMap: { [key: number]: string } = {
      28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
      80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
      14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
      9648: 'Mystery', 10749: 'Romance', 878: 'Science Fiction',
      10770: 'TV Movie', 53: 'Thriller', 10752: 'War', 37: 'Western'
    };

    const genreNames = genreIds
      .map(id => genreMap[id])
      .filter(name => name)
      .slice(0, 3); // Take first 3 genres

    return genreNames.length > 0 ? genreNames.join('/') : 'Drama';
  }

  async getMovieById(id: string): Promise<any> {
    try {
      const movieData = await getMovieDetails(id);

      // Convert to legacy format for compatibility
      return {
        ...movieData,
        Title: movieData.title,
        Year: movieData.release_date ? movieData.release_date.split('-')[0] : '',
        Poster: movieData.poster_path ? `https://image.tmdb.org/t/p/w500${movieData.poster_path}` : 'N/A',
        Response: 'True',
        imdbID: `tmdb_${movieData.id}`
      };
    } catch (error) {
      console.error('Error getting movie by ID via Functions:', error);
      throw error;
    }
  }

  async getMovieByTitle(title: string, year?: string): Promise<any> {
    try {
      const searchQuery = year ? `${title} ${year}` : title;
      const searchResult = await searchMovies(searchQuery, 1);

      if (searchResult.results.length === 0) {
        throw new Error(`Movie not found: ${title}`);
      }

      // Find best match by year if provided
      let bestMatch = searchResult.results[0];
      if (year) {
        const yearMatch = searchResult.results.find(movie =>
          movie.release_date && movie.release_date.startsWith(year)
        );
        if (yearMatch) {
          bestMatch = yearMatch;
        }
      }

      // Get full details
      return this.getMovieById(bestMatch.id.toString());
    } catch (error) {
      console.error('Error getting movie by title via Functions:', error);
      throw error;
    }
  }

  async getTrendingMovies(timeWindow: 'day' | 'week' = 'week'): Promise<any[]> {
    try {
      const data = await getTrendingMovies(timeWindow);
      return data.results || [];
    } catch (error) {
      console.error('Error getting trending movies via Functions:', error);
      return [];
    }
  }

  async getRecentlyAddedMovies(): Promise<any[]> {
    try {
      const data = await getRecentlyAddedMovies();
      // Convert to expected format for compatibility
      return data.results.map(movie => ({
        ...movie,
        Title: movie.title,
        Year: movie.release_date ? movie.release_date.split('-')[0] : '',
        imdbID: `tmdb_${movie.id}`,
        Poster: movie.poster_path ? `https://image.tmdb.org/t/p/w500${movie.poster_path}` : null,
        Response: 'True'
      }));
    } catch (error) {
      console.error('Failed to fetch recently added movies from Functions:', error);
      return [];
    }
  }

  async getGenres(): Promise<Array<{ id: number; name: string }>> {
    try {
      const data = await getGenres();
      return data.genres || [];
    } catch (error) {
      console.error('Error getting genres via Functions:', error);
      return [];
    }
  }

  // =============== PEOPLE METHODS ===============

  async searchPeople(query: string, page: number = 1): Promise<PersonSearchResult> {
    try {
      return await searchPeople(query, page);
    } catch (error) {
      console.error('Error searching people via Functions:', error);
      return {
        page: 1,
        results: [],
        total_pages: 0,
        total_results: 0
      };
    }
  }

  async getPersonById(personId: string | number): Promise<PersonDetails | null> {
    try {
      return await getPersonDetails(personId);
    } catch (error) {
      console.error('Error getting person by ID via Functions:', error);
      return null;
    }
  }

  async getPersonByName(name: string): Promise<PersonDetails | null> {
    try {
      const searchResult = await searchPeople(name, 1);

      if (searchResult.results.length === 0) {
        return null;
      }

      // Get full details for the first result
      return await getPersonDetails(searchResult.results[0].id);
    } catch (error) {
      console.error('Error getting person by name via Functions:', error);
      return null;
    }
  }

  // =============== UTILITY METHODS ===============

  isApiKeyValid(): boolean {
    return this.apiKeyValid;
  }

  getHighQualityPoster(posterPath: string): string {
    if (!posterPath) return '';
    return posterPath.startsWith('http') ? posterPath : `https://image.tmdb.org/t/p/w500${posterPath}`;
  }

  getBackdropImage(backdropPath: string): string {
    if (!backdropPath) return '';
    return backdropPath.startsWith('http') ? backdropPath : `https://image.tmdb.org/t/p/w1280${backdropPath}`;
  }

  getPersonProfileImage(profilePath: string | null, size: 'w185' | 'h632' | 'original' = 'h632'): string {
    if (!profilePath) return '';
    return `https://image.tmdb.org/t/p/${size}${profilePath}`;
  }

  getMultiplePosters(movie: any): string[] {
    const posters: string[] = [];
    if (movie.poster_path) {
      posters.push(`https://image.tmdb.org/t/p/w500${movie.poster_path}`);
    }
    if (movie.Poster && movie.Poster !== 'N/A') {
      posters.push(movie.Poster);
    }
    return posters;
  }

  // =============== VALIDATION METHODS ===============

  private async validateApiKey(): Promise<boolean> {
    try {
      await getConfiguration();
      this.apiKeyValid = true;
      return true;
    } catch (error) {
      console.warn('Firebase Functions API validation failed:', error);
      this.apiKeyValid = false;
      return false;
    }
  }

  // Get a mix of popular and top-rated movies by languages with proper language interleaving
  async getPopularAndTopRatedMoviesByLanguages(languages: string[] = ['en'], totalCount: number = 50, page: number = 1): Promise<any[]> {
    try {
      console.log(`🎬 TMDbFunctionsService.getPopularAndTopRatedMoviesByLanguages called for languages: ${languages.join(', ')}, count: ${totalCount}, page: ${page}`);

      // Call Firebase Functions API directly
      return await httpRequest('getPopularAndTopRatedMoviesByLanguages', {
        languages: languages.join(','),
        count: totalCount.toString(),
        page: page.toString()
      });
    } catch (error) {
      console.error('Error fetching popular and top-rated movies by languages via Functions:', error);
      return [];
    }
  }

}

// Create singleton instance
export const tmdbFunctionsService = new TMDbFunctionsService();

// API health check
export async function checkFunctionsHealth(): Promise<boolean> {
  try {
    await getConfiguration();
    return true;
  } catch (error) {
    console.warn('Functions API health check failed:', error);
    return false;
  }
}

// Fire cheap pings to keep getMovieDetails warm — cached movie returns instantly
export function warmUpMovieFunctions(): void {
  void getMovieDetails(278, { includeCredits: false, includeVideos: false }).catch(() => {});
} 